import { useEffect, useRef, useState } from "react";
import { Aircraft, AircraftWbStation } from "../../models/aircraft";
import { FlightWbLoad } from "../../models/flight";

/**
 * Cabin seat map — the graphical alternative to the loadsheet list.
 *
 * Airline-seat-map language (the pattern ForeFlight/Garmin converge on): a top-down seat
 * glyph on a strict grid inside a plain cabin frame. Entirely data-driven from the W&B
 * profile stations: arm → row (clustered ±15), latArm → position in row when present,
 * otherwise the profile's array order (cosmetic only — the math always uses arm/latArm).
 *
 * Interactions (edit mode): tap a person → tap another seat to move/swap · tap an empty
 * seat to strap cargo on it (FlightWbLoad.loadType 'other') · tap cargo to edit/move/
 * remove · tap a baggage bay to load it (over-max allowed, flags red — same rule as the
 * list; never silently clamped).
 */

type SeatOcc = { kind: "person" | "cargo"; name: string; mass: number } | null;

type PopState =
    | { mode: "seat-add" | "seat-edit"; stationId: string; x: number; y: number }
    | { mode: "bag"; stationId: string; x: number; y: number; h?: number }; //h = glyph height the popover clears

interface WbSeatMapProps {
    aircraft: Aircraft;
    wbLoads: FlightWbLoad[];
    canEdit: boolean;
    onSwapSeats: (stationIdA: string, stationIdB: string) => void;
    onPlaceCargo: (station: AircraftWbStation, mass: number) => void;
    onRemoveLoad: (stationId: string) => void;
    onSetBagMass: (station: AircraftWbStation, mass: number) => void;
}

//geometry constants (match the approved mockup)
const SEAT = 46, GAP = 2, AISLE = 26, ROW_H = 86, BAG_H = 40, TOP = 86;
const UNDER_H = 19, UNDER_GAP = 11; //under-seat bay strip + the extra row height it needs
const POP_W = 248; //keep in sync with .wb-map-pop width

function WbSeatMap({ aircraft, wbLoads, canEdit, onSwapSeats, onPlaceCargo, onRemoveLoad, onSetBagMass }: WbSeatMapProps) {
    const stations = aircraft.wb!.stations;
    const seats = stations.filter((s) => s.type === "seat");
    const bags = stations.filter((s) => s.type === "baggage");
    const isHeli = (aircraft.wb!.latEnvelopePoints?.length ?? 0) > 0;

    const [sel, setSel] = useState<string | null>(null);
    const [pop, setPop] = useState<PopState | null>(null);
    const [popMass, setPopMass] = useState<number>(20);
    const popInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (pop) {
            popInputRef.current?.focus();
            popInputRef.current?.select();
        }
    }, [pop]);

    // ── layout: cluster seats into rows by arm; latArm orders a row when present ──
    const rows: { arm: number; seats: AircraftWbStation[] }[] = [];
    [...seats].sort((a, b) => a.arm - b.arm).forEach((s) => {
        const row = rows.find((r) => Math.abs(r.arm - s.arm) <= 15);
        if (row) row.seats.push(s);
        else rows.push({ arm: s.arm, seats: [s] });
    });
    rows.forEach((r) => {
        if (r.seats.some((s) => s.latArm !== undefined)) {
            r.seats.sort((a, b) => (a.latArm ?? 0) - (b.latArm ?? 0));
        } else {
            r.seats.sort((a, b) => seats.indexOf(a) - seats.indexOf(b)); //profile order = cabin order
        }
    });

    // ── bays that physically live under a seat: drawn attached to it, not as a separate row ──
    //the profile declares the pairing via combinedLimitWith (the same placard link the limit
    //check uses); a bay without one is a standalone hold and keeps the full-width bay row.
    const underBySeat = new Map<string, AircraftWbStation>();
    bags.forEach((b) => {
        const seatId = b.combinedLimitWith?.stationId;
        if (seatId && seats.some((s) => s.id === seatId) && !underBySeat.has(seatId)) underBySeat.set(seatId, b);
    });
    const underIds = new Set(Array.from(underBySeat.values()).map((b) => b.id));
    const holdBags = bags.filter((b) => !underIds.has(b.id));

    const rowW = (n: number) => (n === 2 ? 2 * SEAT + AISLE : n * SEAT + (n - 1) * GAP);
    const maxN = Math.max(1, ...rows.map((r) => r.seats.length));
    const cabinW = Math.max(rowW(maxN) + 56, 210);
    const W = cabinW + 24;

    const rowHeights = rows.map((r) => ROW_H + (r.seats.some((s) => underBySeat.has(s.id)) ? UNDER_H + UNDER_GAP : 0));
    const rowTop = (ri: number) => TOP + rowHeights.slice(0, ri).reduce((a, h) => a + h, 0);
    const seatsBlockH = rowHeights.reduce((a, h) => a + h, 0);

    const H = TOP + seatsBlockH + holdBags.length * (BAG_H + 14) + 56;
    const cx = W / 2, cabX = (W - cabinW) / 2;

    const occFor = (stationId: string): SeatOcc => {
        const l = wbLoads.find((x) => x.station.id === stationId);
        if (!l) return null;
        if (l.loadType === "other") return { kind: "cargo", name: l.name || "Cargo", mass: l.mass };
        if (!l.name && l.mass <= 0) return null; //empty remnant from the list's stepper
        return { kind: "person", name: l.name || "—", mass: l.mass };
    };
    const bagMass = (stationId: string) => wbLoads.find((x) => x.station.id === stationId)?.mass ?? 0;

    const seatClick = (station: AircraftWbStation, x: number, y: number) => {
        if (!canEdit) return;
        const occ = occFor(station.id);
        if (sel !== null) {
            if (sel !== station.id) onSwapSeats(sel, station.id);
            setSel(null);
            setPop(null);
            return;
        }
        if (!occ) {
            setPopMass(20);
            setPop({ mode: "seat-add", stationId: station.id, x, y });
        } else if (occ.kind === "cargo") {
            setPopMass(occ.mass);
            setPop({ mode: "seat-edit", stationId: station.id, x, y });
        } else {
            setPop(null);
            setSel(station.id);
        }
    };

    const bagClick = (station: AircraftWbStation, x: number, y: number, h?: number) => {
        if (!canEdit || sel !== null) return;
        setPopMass(bagMass(station.id) || 10);
        setPop({ mode: "bag", stationId: station.id, x, y, h });
    };

    const popStation = pop ? stations.find((s) => s.id === pop.stationId) : undefined;
    const selOcc = sel ? occFor(sel) : null;

    const confirmPop = () => {
        if (!pop || !popStation) return;
        if (pop.mode === "bag") {
            onSetBagMass(popStation, Math.max(0, Math.round(popMass)));
        } else {
            onPlaceCargo(popStation, popMass);
        }
        setPop(null);
    };

    // ── render ──
    return (
        <div className="wb-map">
            <div className="wb-map-inner" style={{ width: W, height: H }}>
                <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H}>
                    {/* cabin frame: crisp geometry, tapered nose */}
                    <path
                        d={`M${cabX + 26},${TOP - 58} L${cabX + cabinW - 26},${TOP - 58}
                            Q${cabX + cabinW},${TOP - 56} ${cabX + cabinW},${TOP - 26} L${cabX + cabinW},${H - 46}
                            Q${cabX + cabinW},${H - 22} ${cabX + cabinW - 24},${H - 22} L${cabX + 24},${H - 22}
                            Q${cabX},${H - 22} ${cabX},${H - 46} L${cabX},${TOP - 26} Q${cabX},${TOP - 56} ${cabX + 26},${TOP - 58} Z`}
                        fill="#fff" stroke="#c7d2dc" strokeWidth={1.6}
                    />
                    {/* glareshield / panel + FWD chevron */}
                    <path d={`M${cabX + 14},${TOP - 26} Q${cx},${TOP - 44} ${cabX + cabinW - 14},${TOP - 26}`} fill="none" stroke="#c7d2dc" strokeWidth={1.4} />
                    <text x={cx} y={TOP - 32} textAnchor="middle" fontSize={8} letterSpacing={1.5} fill="#96a2ad" fontFamily="ui-monospace, monospace">PANEL</text>
                    <path d={`M${cx - 7},${TOP - 70} L${cx},${TOP - 78} L${cx + 7},${TOP - 70}`} fill="none" stroke="#6a7885" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    <text x={cx + 14} y={TOP - 70} fontSize={8.5} fill="#96a2ad" fontFamily="ui-monospace, monospace">FWD</text>

                    {/* one hatch pattern shared by every under-seat strip */}
                    <defs>
                        <pattern id="wbhatchU" width={7} height={7} patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                            <line x1={0} y1={0} x2={0} y2={7} stroke="#dde4ea" strokeWidth={2.5} />
                        </pattern>
                    </defs>

                    {rows.map((r, ri) => {
                        const n = r.seats.length, y = rowTop(ri);
                        const total = rowW(n), x0 = cx - total / 2;
                        return r.seats.map((st, i) => {
                            const x = n === 2 ? x0 + i * (SEAT + AISLE) : x0 + i * (SEAT + GAP);
                            const under = underBySeat.get(st.id);
                            const uy = y + SEAT * 0.94 + 30;
                            return (
                                <g key={st.id}>
                                    <SeatGlyph station={st} x={x} y={y} occ={occFor(st.id)}
                                        selState={sel === st.id ? "sel" : sel ? "target" : null}
                                        clickable={canEdit} massUnit={aircraft.massUnit} onClick={() => seatClick(st, x, y)} />
                                    {under && (
                                        <UnderSeatBag station={under} x={x} y={uy} mass={bagMass(under.id)}
                                            seatMass={occFor(st.id)?.mass ?? 0} massUnit={aircraft.massUnit}
                                            clickable={canEdit && sel === null} onClick={() => bagClick(under, x, uy, UNDER_H)} />
                                    )}
                                </g>
                            );
                        });
                    })}

                    {holdBags.map((b, bi) => {
                        const y = TOP + seatsBlockH + bi * (BAG_H + 14) - 8;
                        const bw = cabinW - 56, x = cx - bw / 2;
                        const m = bagMass(b.id), hasMax = b.maxMass > 0, over = hasMax && m > b.maxMass, loaded = m > 0;
                        const stroke = over ? "#b3261e" : loaded ? "#8b98a5" : "#c7d2dc";
                        const fillW = hasMax ? Math.min(1, m / b.maxMass) * (bw - 16) : 0;
                        return (
                            <g key={b.id} onClick={() => bagClick(b, x, y)} style={{ cursor: canEdit ? "pointer" : "default" }}>
                                <defs>
                                    <pattern id={`wbhatch${bi}`} width={7} height={7} patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                                        <line x1={0} y1={0} x2={0} y2={7} stroke="#dde4ea" strokeWidth={2.5} />
                                    </pattern>
                                </defs>
                                <rect x={x} y={y} width={bw} height={BAG_H} rx={8} fill={`url(#wbhatch${bi})`} stroke={stroke} strokeWidth={loaded ? 1.6 : 1.3} />
                                <text x={cx} y={y + BAG_H / 2 - 4} textAnchor="middle" fontSize={9} fontWeight={700} letterSpacing={1}
                                    fill={over ? "#b3261e" : loaded ? "#5b6673" : "#8b98a5"} fontFamily="ui-monospace, monospace">
                                    {b.name.toUpperCase()}{loaded ? ` — ${m} ${aircraft.massUnit.toUpperCase()}` : ""}
                                </text>
                                <text x={cx} y={y + BAG_H / 2 + 9} textAnchor="middle" fontSize={8}
                                    fill={over ? "#b3261e" : "#a6b0ba"} fontFamily="ui-monospace, monospace">
                                    {over ? `OVER MAX ${b.maxMass} ${aircraft.massUnit}`
                                        : hasMax ? `max ${b.maxMass} ${aircraft.massUnit}${loaded || !canEdit ? "" : " · tap to load"}`
                                        : (loaded || !canEdit ? "" : "tap to load")}
                                </text>
                                {/* fill bar only when the bay HAS a max to fill against */}
                                {loaded && hasMax && (
                                    <>
                                        <rect x={x + 8} y={y + BAG_H - 7} width={bw - 16} height={3} rx={1.5} fill="#eef1f5" />
                                        <rect x={x + 8} y={y + BAG_H - 7} width={fillW} height={3} rx={1.5} fill={over ? "#b3261e" : "#1e5fae"} />
                                    </>
                                )}
                            </g>
                        );
                    })}

                    {isHeli && (
                        <text x={cx} y={H - 6} textAnchor="middle" fontSize={7.5} letterSpacing={0.5} fill="#b4bec7" fontFamily="ui-monospace, monospace">
                            TAIL ROTOR AFT — BRIEF PAX: APPROACH FROM FRONT
                        </text>
                    )}
                </svg>

                {/* move-in-progress prompt: a person/cargo is selected, waiting for the destination */}
                {sel && selOcc && (
                    <div className="wb-map-moving">
                        Moving <b>{selOcc.kind === "cargo" ? "Cargo" : selOcc.name}</b> — tap the new position
                        <button title="Cancel move" onClick={() => setSel(null)}>✕</button>
                    </div>
                )}

                {pop && popStation && (
                    /* keep the popover centered on its seat but inside the map (POP_W matches .wb-map-pop) */
                    <div className="wb-map-pop" style={{ left: Math.max(4, Math.min(pop.x + SEAT / 2 - POP_W / 2, W - POP_W - 4)), top: pop.y + (pop.mode === "bag" ? (pop.h ?? BAG_H) + 8 : SEAT + 30) }}>
                        <h5>
                            {pop.mode === "bag" ? popStation.name + (popStation.maxMass > 0 ? ` · max ${popStation.maxMass} ${aircraft.massUnit}` : "")
                                : pop.mode === "seat-edit" ? "Cargo on this seat" : `Cargo on ${popStation.name}`}
                        </h5>
                        <div className="wb-map-pop-row">
                            <input ref={popInputRef} type="number" min={0} value={popMass}
                                onChange={(e) => setPopMass(parseInt(e.target.value) || 0)}
                                onKeyDown={(e) => { if (e.key === "Enter") confirmPop(); }} />
                            <span>{aircraft.massUnit}{pop.mode !== "bag" ? " · strapped" : ""}</span>
                        </div>
                        <div className="wb-map-pop-chips">
                            {[10, 20, 40].map((q) => (
                                <button key={q} onClick={() => setPopMass(q)}>{q}</button>
                            ))}
                            {pop.mode === "bag" && popStation.maxMass > 0 && <button onClick={() => setPopMass(popStation.maxMass)}>max</button>}
                        </div>
                        <div className="wb-map-pop-btns">
                            <button className="wb-map-pop-add" onClick={confirmPop}>
                                {pop.mode === "seat-add" ? "Add" : "Update"}
                            </button>
                            {pop.mode === "seat-edit" && (
                                <>
                                    <button className="wb-map-pop-mv" onClick={() => { setSel(pop.stationId); setPop(null); }}>Move</button>
                                    <button className="wb-map-pop-rm" onClick={() => { onRemoveLoad(pop.stationId); setPop(null); }}>Remove</button>
                                </>
                            )}
                            {pop.mode === "bag" && bagMass(pop.stationId) > 0 && (
                                <button className="wb-map-pop-rm" onClick={() => { if (popStation.type === "baggage") onSetBagMass(popStation, 0); setPop(null); }}>Clear</button>
                            )}
                            <button className="wb-map-pop-cnl" onClick={() => setPop(null)}>✕</button>
                        </div>
                        <div className="wb-map-pop-hint">
                            {pop.mode === "bag"
                                ? "Over-max is allowed but flags red and blocks an OK save."
                                : pop.mode === "seat-edit" ? "Move: then tap the destination seat." : "Passenger seating is moved by tapping an occupied seat first."}
                        </div>
                    </div>
                )}
            </div>
            {canEdit && <div className="wb-map-help">Tap a person, then a seat, to move or swap · tap an empty seat to strap cargo on it</div>}
        </div>
    );
}

export default WbSeatMap;

// ─── under-seat bay: a strip tucked beneath its seat, the width of the seat ───

interface UnderSeatBagProps {
    station: AircraftWbStation;
    x: number;
    y: number;
    mass: number;
    seatMass: number; //occupant/cargo on the seat above, for the combined placard check
    massUnit: string;
    clickable: boolean;
    onClick: () => void;
}

function UnderSeatBag({ station, x, y, mass, seatMass, massUnit, clickable, onClick }: UnderSeatBagProps) {
    const loaded = mass > 0;
    const overOwn = station.maxMass > 0 && mass > station.maxMass;
    //the placard the seat and the bay share — busting it colours the strip even when the bay
    //itself is under its own max, which is the whole point of showing them together
    const combined = station.combinedLimitWith;
    const overCombined = combined !== undefined && seatMass + mass > combined.maxMass;
    const bad = overOwn || overCombined;

    const stroke = bad ? "#b3261e" : loaded ? "#8b98a5" : "#c7d2dc";
    const text = bad ? "#b3261e" : loaded ? "#5b6673" : "#a6b0ba";

    return (
        <g onClick={onClick} style={{ cursor: clickable ? "pointer" : "default" }}>
            <title>
                {station.name}
                {station.maxMass > 0 ? ` · max ${station.maxMass} ${massUnit}` : ""}
                {combined ? ` · with seat max ${combined.maxMass} ${massUnit}` : ""}
            </title>
            <rect x={x} y={y} width={SEAT} height={UNDER_H} rx={5}
                fill={loaded ? "#fff" : "url(#wbhatchU)"} stroke={stroke} strokeWidth={loaded ? 1.5 : 1.2} />
            {/* handle nub, so the strip reads as a bag rather than a slot */}
            <path d={`M${x + SEAT / 2 - 5},${y + 3.5} q5,-4 10,0`} fill="none" stroke={stroke} strokeWidth={1.2} strokeLinecap="round" />
            <text x={x + SEAT / 2} y={y + UNDER_H - 5} textAnchor="middle" fontSize={8}
                fontWeight={loaded ? 700 : 600} fill={text} fontFamily="ui-monospace, monospace">
                {loaded ? `${mass} ${massUnit}` : "BAG"}
            </text>
            <rect x={x - 3} y={y - 3} width={SEAT + 6} height={UNDER_H + 6} fill="transparent" />
        </g>
    );
}

// ─── seat glyph: top-down cushion + backrest + armrests (backrest = aft edge) ───

interface SeatGlyphProps {
    station: AircraftWbStation;
    x: number;
    y: number;
    occ: SeatOcc;
    selState: "sel" | "target" | null;
    clickable: boolean;
    massUnit: string;
    onClick: () => void;
}

function SeatGlyph({ station, x, y, occ, selState, clickable, massUnit, onClick }: SeatGlyphProps) {
    const w = SEAT, h = SEAT * 0.94, arm = SEAT * 0.10, cush = SEAT - 2 * arm - 4;
    const person = occ?.kind === "person", cargo = occ?.kind === "cargo";
    const stroke = person ? "#174e91" : cargo ? "#5b6673" : "#c2ccd6";
    const cushFill = person ? "#e8f0f9" : cargo ? "#f1f3f6" : "#fdfdfe";
    const backFill = person ? "#1e5fae" : cargo ? "#8b98a5" : "#eef1f5";
    const initials = person ? occ!.name.split(/\s/).map((p) => p[0]).join("").slice(0, 2).toUpperCase() : "";
    const bx = x + w / 2 - w * 0.24, by = y + h * 0.16, bs = w * 0.48;

    return (
        <g onClick={onClick} style={{ cursor: clickable ? "pointer" : "default" }}>
            {selState === "sel" && <rect x={x - 4} y={y - 4} width={w + 8} height={h + 8} rx={10} fill="none" stroke="#1e5fae" strokeWidth={3} strokeOpacity={0.35} />}
            {selState === "target" && <rect x={x - 4} y={y - 4} width={w + 8} height={h + 8} rx={10} fill="none" stroke="#137a52" strokeWidth={2.5} strokeDasharray="4 3" strokeOpacity={0.6} />}
            <rect x={x} y={y + h * 0.22} width={arm} height={h * 0.62} rx={arm / 2} fill="#e6ebf0" stroke={stroke} strokeWidth={1} />
            <rect x={x + w - arm} y={y + h * 0.22} width={arm} height={h * 0.62} rx={arm / 2} fill="#e6ebf0" stroke={stroke} strokeWidth={1} />
            <rect x={x + arm + 2} y={y + h * 0.10} width={cush} height={h * 0.66} rx={7} fill={cushFill} stroke={stroke} strokeWidth={1.4} />
            <rect x={x + arm - 1} y={y + h * 0.74} width={w - 2 * arm + 2} height={h * 0.24} rx={6} fill={backFill} stroke={stroke} strokeWidth={1.4} />
            {cargo && (
                <>
                    <rect x={bx} y={by} width={bs} height={bs} rx={4} fill="#dde3ea" stroke="#5b6673" strokeWidth={1.4} />
                    <line x1={bx + bs / 2} y1={by} x2={bx + bs / 2} y2={by + bs} stroke="#5b6673" strokeWidth={1.2} />
                    <line x1={bx} y1={by + bs / 2} x2={bx + bs} y2={by + bs / 2} stroke="#5b6673" strokeWidth={1.2} />
                    <line x1={x + w / 2 - w * 0.34} y1={y + h * 0.06} x2={bx + 2} y2={by + 3} stroke="#8b98a5" strokeWidth={1.4} />
                    <line x1={x + w / 2 + w * 0.34} y1={y + h * 0.06} x2={bx + bs - 2} y2={by + 3} stroke="#8b98a5" strokeWidth={1.4} />
                </>
            )}
            {person && (
                <>
                    <circle cx={x + w / 2} cy={y + h * 0.40} r={w * 0.20} fill="#1e5fae" />
                    <text x={x + w / 2} y={y + h * 0.40} dy={3.5} textAnchor="middle" fontSize={w * 0.20} fontWeight={700} fill="#fff">{initials}</text>
                </>
            )}
            <text x={x + w / 2} y={y + h + 13} textAnchor="middle" fontSize={8.5} fontWeight={600} fill={occ ? "#333c46" : "#a6b0ba"}>
                {occ ? (person ? occ!.name : "Cargo") : station.name}
            </text>
            {occ && (
                <text x={x + w / 2} y={y + h + 24} textAnchor="middle" fontSize={8} fill="#96a2ad" fontFamily="ui-monospace, monospace">
                    {occ.mass} {massUnit}
                </text>
            )}
            {/* generous hit target */}
            <rect x={x - 5} y={y - 5} width={w + 10} height={h + 28} fill="transparent" />
        </g>
    );
}
